import { getLinkRanges, getLinks } from '@fc-shared/utils/link-utils';
import type { LinkRange } from '@fc-shared/utils/link-utils';
import type { Channel } from '../models/channel';
import type { ChatMessage } from '../models/chat-message';

export { getLinkRanges, getLinks };
export type { LinkRange };

export function getLinkInformation(link: string) {
  const url = new URL(link);
  return {
    title: url.hostname,
  };
}

export interface PreviewIdentity {
  id?: string | null;
  originalLink?: string | null;
  canonicalUrl?: string | null;
}

export function uniquePreviews<T extends PreviewIdentity>(previews: T[]): T[] {
  const seen = new Set<string>();

  return previews.filter((preview) => {
    const key = getPreviewIdentity(preview);
    if (!key) {
      return true;
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function getUnmutedUnreadMessagesCount(channels: Channel[]): number {
  return channels
    .filter((channel) => channel.config?.mute !== true)
    .reduce((total, channel) => total + (channel.unreadMessagesCount ?? 0), 0);
}

function getPreviewIdentity(preview: PreviewIdentity): string {
  if (preview.canonicalUrl) {
    return `canonical:${normalizePreviewUrl(preview.canonicalUrl)}`;
  }
  if (preview.originalLink) {
    return `original:${normalizePreviewUrl(preview.originalLink)}`;
  }
  return preview.id ? `id:${preview.id}` : '';
}

function normalizePreviewUrl(url: string): string {
  return url.trim().replace(/\/$/, '').toLowerCase();
}

// A bot reply someone ran is shown as theirs, so their own view of it is not a
// view by someone else, the same as the author of a regular message.
export function getMessageViewers(message: ChatMessage) {
  const authorId = message.invoker ?? message.author;
  return (message.meta?.viewed ?? []).filter(
    (view) => view.employeeId !== authorId,
  );
}
